import { apiFetchInit, apiUrl } from "@/lib/apiOrigin";
import { isAtwPlatformOrg } from "@/lib/atwPlatformOrg";
import {
  isDashboardOrgSegment,
  readDashboardOrgCookie,
  writeDashboardOrgCookie,
} from "@/lib/dashboardOrg";
import { isValidDirectoryOrgId, postSessionOrgUpdate } from "@/lib/directorySessionOrg";

export type DirectoryOrgContextResult = {
  orgId: string | null;
  source: "session" | "cookie" | "orgs" | "none";
  sessionUpdated: boolean;
  error?: string;
};

type MeOrgRow = {
  orgId?: string;
  domain?: string | null;
  companyName?: string | null;
  role?: string | null;
};

const ORG_ADMIN_ROLES = new Set(["account_owner", "account_admin", "super_admin"]);

let cached: DirectoryOrgContextResult | null = null;
let inflight: Promise<DirectoryOrgContextResult> | null = null;

function asOrgId(raw: string | null | undefined): string | null {
  const v = (raw || "").trim();
  return isDashboardOrgSegment(v) ? v : null;
}

async function fetchMeOrgs(): Promise<MeOrgRow[]> {
  const res = await fetch(apiUrl("/api/me/orgs"), {
    cache: "no-store",
    ...apiFetchInit(),
  });
  if (!res.ok) return [];
  const data = (await res.json()) as { orgs?: MeOrgRow[] };
  return Array.isArray(data.orgs) ? data.orgs : [];
}

function pickDefaultOrgId(orgs: MeOrgRow[]): string | null {
  const valid = orgs
    .map((row) => ({
      orgId: asOrgId(row.orgId),
      role: row.role?.trim() || null,
    }))
    .filter((row): row is { orgId: string; role: string | null } => Boolean(row.orgId));

  if (!valid.length) return null;
  if (valid.length === 1) return valid[0]!.orgId;

  const platform = valid.find((row) => isAtwPlatformOrg(row.orgId));
  if (platform?.role && ORG_ADMIN_ROLES.has(platform.role)) {
    return platform.orgId;
  }

  const admin = valid.find((row) => row.role && ORG_ADMIN_ROLES.has(row.role));
  if (admin) return admin.orgId;

  if (platform) return platform.orgId;
  return valid[0]!.orgId;
}

async function resolveOnce(sessionOrgId: string | null | undefined): Promise<DirectoryOrgContextResult> {
  const fromSession = asOrgId(sessionOrgId);
  if (fromSession) {
    writeDashboardOrgCookie(fromSession);
    return { orgId: fromSession, source: "session", sessionUpdated: false };
  }

  const fromCookie = readDashboardOrgCookie();
  if (fromCookie) {
    try {
      writeDashboardOrgCookie(fromCookie);
      await postSessionOrgUpdate(fromCookie);
      return { orgId: fromCookie, source: "cookie", sessionUpdated: true };
    } catch (error) {
      return {
        orgId: fromCookie,
        source: "cookie",
        sessionUpdated: false,
        error: error instanceof Error ? error.message : "Could not sync organization to session.",
      };
    }
  }

  const orgs = await fetchMeOrgs();
  const picked = pickDefaultOrgId(orgs);
  if (!picked) {
    return {
      orgId: null,
      source: "none",
      sessionUpdated: false,
      error: "No organization is assigned to this account yet.",
    };
  }

  try {
    writeDashboardOrgCookie(picked);
    await postSessionOrgUpdate(picked);
    return { orgId: picked, source: "orgs", sessionUpdated: true };
  } catch (error) {
    return {
      orgId: picked,
      source: "orgs",
      sessionUpdated: false,
      error: error instanceof Error ? error.message : "Could not set active organization.",
    };
  }
}

/**
 * When the JWT has no orgId, pick a membership from /api/me/orgs (or sync from
 * dashboard-org), persist orgId on the shared session, and stamp the cookie for APIs.
 */
export function ensureDirectoryOrgContext(
  sessionOrgId?: string | null,
  opts?: { force?: boolean },
): Promise<DirectoryOrgContextResult> {
  if (!opts?.force && cached?.orgId && isValidDirectoryOrgId(cached.orgId)) {
    return Promise.resolve({ ...cached, sessionUpdated: false });
  }
  if (inflight) return inflight;
  inflight = resolveOnce(sessionOrgId)
    .then((result) => {
      cached = result;
      return result;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function resetDirectoryOrgContextCache(): void {
  cached = null;
  inflight = null;
}
